import { api } from "../api";
import { presetFor } from "../avatar/catalog";
import { avatarPartsReady, drawFrame } from "../avatar/composer";
import { AVATAR_FRAME } from "../config";
import { escapeHtml } from "../moderation/format";
import type { DossierField, PublicDossier } from "../types";
import { isolateKeyboardFromGame } from "./keyboard";

const PREVIEW_SCALE = 3;

export interface DossierViewOptions {
  token: string;
  userId: string;
  /** Quem esta olhando: no proprio dossie, sem "mandar mensagem" e com o que esta oculto marcado. */
  viewerId: string;
  /** Mostra "Mandar mensagem" (falso = mensagens privadas indisponiveis). */
  canMessage: boolean;
}

/** Como a ficha fechou: mandar mensagem, editar o proprio dossie ou so fechar (null). */
export type DossierResult = "message" | "edit" | null;

/**
 * Ficha de leitura do dossie de alguem. Abre por cima de qualquer outro
 * overlay (lista de jogadores, mensagens), como o modal de confirmacao.
 * Resolve ao fechar, dizendo se a pessoa pediu para mandar mensagem ou (no
 * proprio dossie) para editar.
 */
export function showDossier(options: DossierViewOptions): Promise<DossierResult> {
  return new Promise((resolve) => {
    const self = options.userId === options.viewerId;
    const overlay = document.createElement("div");
    overlay.className = "overlay dossier-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Dossiê");
    overlay.innerHTML = `
      <div class="panel dossier-panel">
        <p class="sub">Carregando dossiê…</p>
        <button type="button" class="secondary" data-close>Fechar</button>
      </div>`;
    document.body.append(overlay);
    isolateKeyboardFromGame(overlay);

    const close = (result: DossierResult = null) => {
      window.removeEventListener("keydown", onKey, true);
      overlay.remove();
      resolve(result);
    };
    // Em captura: o Esc fecha so a ficha, nao o overlay de baixo.
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
    };
    window.addEventListener("keydown", onKey, true);
    overlay.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      // clicar fora do painel tambem fecha
      if (target === overlay || target.closest("[data-close]")) close();
      if (target.closest("[data-message]")) close("message");
      if (target.closest("[data-edit]")) close("edit");
    });

    api
      .dossier(options.token, options.userId)
      .then(({ dossier }) => render(overlay, dossier, self, options.canMessage))
      .catch((error: unknown) => {
        const panel = overlay.querySelector(".dossier-panel")!;
        panel.innerHTML = `
          <p class="notice error">${escapeHtml(error instanceof Error ? error.message : "Não foi possível abrir o dossiê.")}</p>
          <button type="button" class="secondary" data-close>Fechar</button>`;
      });
  });
}

function render(overlay: HTMLElement, d: PublicDossier, self: boolean, canMessage: boolean): void {
  const hidden = new Set<DossierField>(d.hidden ?? []);
  const tag = (field: DossierField) => (self && hidden.has(field) ? ` <span class="dossier-hidden">oculto</span>` : "");
  const rows: [string, string][] = [];
  if (d.team) rows.push(["Time", escapeHtml(d.team) + tag("team")]);
  if (d.jobTitle) rows.push(["Cargo", escapeHtml(d.jobTitle) + tag("job")]);
  if (d.age !== undefined) rows.push(["Idade", `${d.age} anos${tag("age")}`]);
  if (d.birthday) rows.push(["Aniversário", escapeHtml(d.birthday) + tag("birthday")]);
  const place = [d.city, d.state].filter(Boolean).join(" – ");
  if (place) rows.push(["Cidade", escapeHtml(place) + tag("location")]);
  const since = new Date(d.memberSince).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  const panel = overlay.querySelector<HTMLDivElement>(".dossier-panel")!;
  panel.innerHTML = `
    <div class="dossier-top">
      <canvas class="dossier-avatar" aria-hidden="true"></canvas>
      <div>
        <p class="dossier-kicker">${self ? "Meu Dossiê" : "Dossiê"}</p>
        <h1>${escapeHtml(d.name)}</h1>
        <p class="dossier-status${d.online ? " online" : ""}">${d.online ? "no Jardim agora" : "fora do Jardim"}</p>
      </div>
    </div>
    ${
      rows.length
        ? `<dl class="dossier-list">${rows.map(([k, v]) => `<div><dt>${k}</dt><dd>${v}</dd></div>`).join("")}</dl>`
        : `<p class="sub dossier-empty">${self ? "Você ainda não preencheu seu dossiê." : "Nada preenchido por aqui ainda."}</p>`
    }
    <p class="sub-hint">No Jardim desde ${escapeHtml(since)}.${self ? " O que está marcado como oculto só você vê." : ""}</p>
    ${self ? `<button type="button" class="primary" data-edit>Editar dossiê</button>` : ""}
    ${!self && canMessage ? `<button type="button" class="primary" data-message>Mandar mensagem</button>` : ""}
    <button type="button" class="secondary" data-close>Fechar</button>
  `;

  const canvas = panel.querySelector<HTMLCanvasElement>(".dossier-avatar")!;
  canvas.width = AVATAR_FRAME.width;
  canvas.height = AVATAR_FRAME.height;
  canvas.style.width = `${AVATAR_FRAME.width * PREVIEW_SCALE}px`;
  canvas.style.height = `${AVATAR_FRAME.height * PREVIEW_SCALE}px`;
  if (avatarPartsReady()) drawFrame(canvas, d.appearance ?? presetFor(d.skin), 0);
}
